import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Table, Tag, Button, Modal, Form, Input, Select, Space, message,
  Drawer, Typography, Popconfirm, Badge, Statistic, Row, Col, Tooltip,
  Divider, Alert, List, Avatar, Upload, Collapse,
} from 'antd';
import {
  PlusOutlined, BugOutlined, ThunderboltOutlined, ReloadOutlined,
  CopyOutlined, CodeOutlined, DeleteOutlined, EditOutlined,
  CommentOutlined, RobotOutlined, CheckCircleOutlined, CloseCircleOutlined,
  MessageOutlined, TagOutlined, UserOutlined, UploadOutlined, LinkOutlined,
  PaperClipOutlined, CheckOutlined,
} from '@ant-design/icons';
import { devRequestsAPI } from '../services/api';

const API_BASE = '/api/v1';
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('spo_auth_token')}` });

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const TYPE_CONFIG = {
  feature:     { color: 'blue',    label: '新功能', icon: <ThunderboltOutlined /> },
  bug:         { color: 'red',     label: 'Bug',    icon: <BugOutlined /> },
  improvement: { color: 'orange',  label: '改善',   icon: <EditOutlined /> },
  question:    { color: 'purple',  label: '問題',   icon: <CommentOutlined /> },
};

const PRIORITY_CONFIG = {
  critical: { color: 'red',    label: '🔥 緊急' },
  high:     { color: 'orange', label: '⬆ 高' },
  medium:   { color: 'blue',   label: '➡ 中' },
  low:      { color: 'green',  label: '⬇ 低' },
};

const STATUS_CONFIG = {
  open:        { color: 'default',    label: '待處理' },
  in_progress: { color: 'processing', label: '處理中' },
  done:        { color: 'success',    label: '完成' },
  rejected:    { color: 'error',      label: '已拒絕' },
  deferred:    { color: 'warning',    label: '延後' },
};

function generateAIPrompt(r) {
  const lines = [
    `## ${r.type === 'bug' ? '🐛 Bug 修復' : '✨ 功能需求'}：${r.title}`,
    ``,
    `**類型**：${TYPE_CONFIG[r.type]?.label || r.type}`,
    `**優先級**：${PRIORITY_CONFIG[r.priority]?.label || r.priority}`,
    `**回報者**：${r.reporter_name || '匿名'}`,
    r.page_url ? `**頁面**：${r.page_url}` : null,
    ``,
    `### 描述`,
    r.description || '（無描述）',
  ];

  if (r.type === 'bug') {
    if (r.steps_to_reproduce) lines.push(``, `### 重現步驟`, r.steps_to_reproduce);
    if (r.current_behavior) lines.push(``, `### 目前行為`, r.current_behavior);
    if (r.expected_behavior) lines.push(``, `### 預期行為`, r.expected_behavior);
  } else {
    if (r.expected_behavior) lines.push(``, `### 期望效果`, r.expected_behavior);
  }

  if (r.tags?.length) lines.push(``, `**標籤**：${r.tags.join(', ')}`);

  lines.push(
    ``,
    `---`,
    ``,
    `請根據以上需求，在 SPO (MarCom AI) 平台的 codebase 中實作此${r.type === 'bug' ? '修復' : '功能'}。`,
    `技術棧：FastAPI (Python 3.11) + PostgreSQL + React 18 + Ant Design 5。`,
    r.type === 'bug'
      ? `找出 bug 根因，提供最小化修復方案，不要引入不必要的重構。`
      : `實作時只加必要的程式，不過度設計，不加不必要的抽象層。`,
  );

  return lines.filter(l => l !== null).join('\n');
}

export default function DevRequestsPage({ currentUser }) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [search, setSearch] = useState('');
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [detailDrawer, setDetailDrawer] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [commentInput, setCommentInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const isStaff = currentUser?.role === 'admin' || currentUser?.role === 'manager';

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.type = filterType;
      if (filterPriority) params.priority = filterPriority;
      if (search) params.q = search;
      params.limit = 200;
      const [listRes, statsRes] = await Promise.all([
        devRequestsAPI.list(params),
        devRequestsAPI.stats(),
      ]);
      const body = listRes.data ?? listRes;
      setItems(body.items || []);
      setStats(statsRes.data ?? statsRes);
    } catch (e) {
      message.error('載入失敗：' + e.message);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType, filterPriority, search]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Load previously uploaded files
  useEffect(() => {
    fetch(`${API_BASE}/api-docs`, { headers: authH() })
      .then(r => r.json())
      .then(list => setUploadedFiles(Array.isArray(list) ? list.slice(0, 20) : []))
      .catch(() => {});
  }, []);

  const handleUpload = async ({ file }) => {
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API_BASE}/api-docs`, {
        method: 'POST',
        headers: authH(),
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || '上傳失敗');
      const shareUrl = `${window.location.origin}${data.raw_url}`;
      setUploadedFiles(prev => [{ ...data, share_url: shareUrl }, ...prev]);
      message.success('上傳成功！連結已可複製');
    } catch (e) {
      message.error('上傳失敗：' + e.message);
    } finally {
      setUploading(false);
    }
    return false; // prevent default antd upload
  };

  const copyFileLink = (entry) => {
    const url = entry.share_url || `${window.location.origin}${API_BASE}/api-docs/${entry.id}/raw`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(entry.id);
      message.success('連結已複製！');
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const refreshDetail = async (id) => {
    try {
      const res = await devRequestsAPI.get(id);
      setDetailDrawer(res.data ?? res);
    } catch (e) {}
  };

  const handleCreate = async (values) => {
    setSubmitting(true);
    try {
      await devRequestsAPI.create({
        ...values,
        tags: values.tags ? values.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        reporter_name: currentUser?.display_name,
      });
      message.success('已新增需求！');
      setCreateModal(false);
      form.resetFields();
      fetchAll();
    } catch (e) {
      message.error('新增失敗：' + e.message);
    } finally { setSubmitting(false); }
  };

  const handleEdit = async (values) => {
    if (!detailDrawer) return;
    setSubmitting(true);
    try {
      const updated = await devRequestsAPI.update(detailDrawer.id, {
        ...values,
        tags: values.tags ? values.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      });
      message.success('已更新');
      setEditModal(false);
      setDetailDrawer(updated.data ?? updated);
      fetchAll();
    } catch (e) {
      message.error('更新失敗：' + e.message);
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    try {
      await devRequestsAPI.delete(id);
      message.success('已刪除');
      setDetailDrawer(null);
      fetchAll();
    } catch (e) { message.error('刪除失敗：' + e.message); }
  };

  const handleComment = async () => {
    if (!commentInput.trim() || !detailDrawer) return;
    setSubmitting(true);
    try {
      await devRequestsAPI.addComment(detailDrawer.id, commentInput.trim());
      setCommentInput('');
      refreshDetail(detailDrawer.id);
    } catch (e) { message.error('留言失敗：' + e.message); }
    finally { setSubmitting(false); }
  };

  const handleCopyPrompt = (r) => {
    const prompt = generateAIPrompt(r);
    navigator.clipboard.writeText(prompt).then(() => {
      message.success('AI Prompt 已複製到剪貼簿，可直接貼入 Claude / ChatGPT！');
    }).catch(() => {
      Modal.info({
        title: 'AI Prompt（手動複製）',
        content: <pre style={{ maxHeight: 400, overflow: 'auto', fontSize: 12 }}>{prompt}</pre>,
        width: 700,
      });
    });
  };

  const handleCopyRecord = (r) => {
    const text = [
      `[SPO #${r.id}] ${r.title}`,
      `類型：${TYPE_CONFIG[r.type]?.label} | 優先級：${PRIORITY_CONFIG[r.priority]?.label} | 狀態：${STATUS_CONFIG[r.status]?.label}`,
      r.description ? `描述：${r.description}` : null,
      `回報者：${r.reporter_name || '—'} | 時間：${r.created_at ? new Date(r.created_at).toLocaleString('zh-TW') : '—'}`,
    ].filter(Boolean).join('\n');
    navigator.clipboard.writeText(text).then(() => message.success('已複製記錄'));
  };

  const openDetail = async (r) => {
    await refreshDetail(r.id);
  };

  const openEdit = () => {
    if (!detailDrawer) return;
    editForm.setFieldsValue({
      ...detailDrawer,
      tags: (detailDrawer.tags || []).join(', '),
    });
    setEditModal(true);
  };

  const columns = [
    {
      title: '類型', dataIndex: 'type', width: 80, align: 'center',
      render: t => {
        const c = TYPE_CONFIG[t] || {};
        return <Tag color={c.color}>{c.icon} {c.label}</Tag>;
      },
    },
    {
      title: '優先', dataIndex: 'priority', width: 80, align: 'center',
      render: p => <Tag color={PRIORITY_CONFIG[p]?.color}>{PRIORITY_CONFIG[p]?.label}</Tag>,
    },
    {
      title: '標題', dataIndex: 'title',
      render: (v, r) => (
        <div>
          <Button type="link" style={{ padding: 0, fontWeight: 500, textAlign: 'left', height: 'auto' }}
                  onClick={() => openDetail(r)}>{v}</Button>
          {r.tags?.length > 0 && (
            <div>{r.tags.map(t => <Tag key={t} style={{ fontSize: 11, marginTop: 2 }}>{t}</Tag>)}</div>
          )}
        </div>
      ),
    },
    {
      title: '狀態', dataIndex: 'status', width: 100,
      render: s => <Tag color={STATUS_CONFIG[s]?.color}>{STATUS_CONFIG[s]?.label}</Tag>,
    },
    {
      title: '回報者', dataIndex: 'reporter_name', width: 100,
      render: v => v || '—',
    },
    {
      title: '時間', dataIndex: 'created_at', width: 140,
      render: v => v ? new Date(v).toLocaleString('zh-TW') : '—',
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      defaultSortOrder: 'descend',
    },
    {
      title: '操作', width: 130,
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="複製記錄"><Button size="small" icon={<CopyOutlined />} onClick={() => handleCopyRecord(r)} /></Tooltip>
          <Tooltip title="複製 AI Prompt"><Button size="small" icon={<RobotOutlined />} onClick={() => handleCopyPrompt(r)} /></Tooltip>
          {isStaff && (
            <Popconfirm title="刪除？" onConfirm={() => handleDelete(r.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const FormContent = ({ type }) => (
    <>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name="type" label="類型" rules={[{ required: true }]}>
            <Select>
              {Object.entries(TYPE_CONFIG).map(([k, v]) => (
                <Select.Option key={k} value={k}>{v.icon} {v.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="priority" label="優先級" rules={[{ required: true }]}>
            <Select>
              {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
                <Select.Option key={k} value={k}><Tag color={v.color}>{v.label}</Tag></Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="title" label="標題" rules={[{ required: true, message: '請輸入標題' }]}>
        <Input placeholder="一句話描述需求或問題" />
      </Form.Item>
      <Form.Item name="description" label="詳細描述">
        <TextArea rows={3} placeholder="提供背景、詳細說明、使用情境..." />
      </Form.Item>
      <Form.Item shouldUpdate={(prev, cur) => prev.type !== cur.type} noStyle>
        {({ getFieldValue }) => getFieldValue('type') === 'bug' && (
          <>
            <Form.Item name="steps_to_reproduce" label="重現步驟">
              <TextArea rows={3} placeholder="1. 進入 XX 頁面&#10;2. 點擊 YY 按鈕&#10;3. 觀察到..." />
            </Form.Item>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="current_behavior" label="目前行為">
                  <TextArea rows={2} placeholder="目前發生什麼..." />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="expected_behavior" label="預期行為">
                  <TextArea rows={2} placeholder="應該要怎樣..." />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
      </Form.Item>
      <Row gutter={12}>
        <Col span={16}>
          <Form.Item name="page_url" label="發生頁面">
            <Input placeholder="/login, /users, /aeo-scanner..." />
          </Form.Item>
        </Col>
        <Col span={8}>
          {type === 'edit' && isStaff && (
            <Form.Item name="status" label="狀態">
              <Select>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <Select.Option key={k} value={k}>{v.label}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </Col>
      </Row>
      <Form.Item name="tags" label="標籤（用逗號分隔）">
        <Input placeholder="frontend, auth, UI..." />
      </Form.Item>
      {type === 'edit' && isStaff && (
        <Form.Item name="assignee" label="指派給">
          <Input placeholder="開發者名稱" />
        </Form.Item>
      )}
    </>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            <BugOutlined /> 需求 & Bug 追蹤
          </Title>
          <Text type="secondary">記錄平台開發需求、Bug 回報，並產生 AI 開發 Prompt</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => {
          form.resetFields();
          form.setFieldsValue({ type: 'feature', priority: 'medium' });
          setCreateModal(true);
        }}>
          新增記錄
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          {[
            { label: '全部', val: stats.total, color: '#1677ff' },
            { label: '待處理', val: (stats.by_status?.open || 0), color: '#faad14' },
            { label: '處理中', val: (stats.by_status?.in_progress || 0), color: '#1890ff' },
            { label: 'Bug', val: (stats.by_type?.bug || 0), color: '#f5222d' },
            { label: '新功能', val: (stats.by_type?.feature || 0), color: '#52c41a' },
            { label: '緊急', val: (stats.by_priority?.critical || 0), color: '#cf1322' },
          ].map(s => (
            <Col key={s.label} xs={8} sm={4}>
              <Card size="small" style={{ textAlign: 'center' }}>
                <Statistic title={s.label} value={s.val} valueStyle={{ color: s.color, fontSize: 20 }} />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* File Upload — secure shareable links */}
      <Collapse size="small" style={{ marginBottom: 12 }} items={[{
        key: 'upload',
        label: <Space><PaperClipOutlined /> 上傳開發附件（產生安全連結）</Space>,
        children: (
          <div>
            <Upload.Dragger
              customRequest={handleUpload}
              showUploadList={false}
              multiple={false}
              style={{ marginBottom: 12 }}
              accept=".md,.txt,.json,.yaml,.yml,.pdf,.html,.png,.jpg,.jpeg,.gif,.docx,.xlsx"
            >
              <p className="ant-upload-drag-icon">
                {uploading ? <span style={{ fontSize: 28 }}>⏳</span> : <UploadOutlined style={{ fontSize: 28, color: '#1890ff' }} />}
              </p>
              <p style={{ margin: '4px 0', fontWeight: 500 }}>點擊或拖拉上傳開發附件</p>
              <p style={{ color: '#999', fontSize: 12 }}>支援 PDF、圖片、Markdown、Excel、Word、JSON 等 · 上限 10MB</p>
            </Upload.Dragger>

            {uploadedFiles.length > 0 && (
              <List
                size="small"
                dataSource={uploadedFiles}
                renderItem={entry => {
                  const url = entry.share_url || `${window.location.origin}${API_BASE}/api-docs/${entry.id}/raw`;
                  return (
                    <List.Item
                      actions={[
                        <Tooltip title={copiedId === entry.id ? '已複製！' : '複製安全連結'}>
                          <Button
                            size="small"
                            icon={copiedId === entry.id ? <CheckOutlined style={{ color: '#52c41a' }} /> : <LinkOutlined />}
                            onClick={() => copyFileLink(entry)}
                            type={copiedId === entry.id ? 'default' : 'primary'}
                            ghost={copiedId !== entry.id}
                          >
                            {copiedId === entry.id ? '已複製' : '複製連結'}
                          </Button>
                        </Tooltip>,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<PaperClipOutlined style={{ fontSize: 16, color: '#1890ff', marginTop: 3 }} />}
                        title={<Text style={{ fontSize: 13 }}>{entry.filename}</Text>}
                        description={
                          <Space size={4} wrap>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {entry.size ? `${(entry.size / 1024).toFixed(1)} KB` : ''}
                            </Text>
                            {entry.uploaded_at && (
                              <Text type="secondary" style={{ fontSize: 11 }}>
                                · {new Date(entry.uploaded_at).toLocaleString('zh-TW')}
                              </Text>
                            )}
                            <Text copyable={{ text: url }} style={{ fontSize: 11, color: '#1890ff', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>
                              {url}
                            </Text>
                          </Space>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            )}
          </div>
        ),
      }]} />

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Input.Search placeholder="搜尋標題..." allowClear style={{ width: 200 }}
                        onSearch={v => setSearch(v)} onChange={e => !e.target.value && setSearch('')} />
          <Select value={filterStatus || undefined} onChange={v => setFilterStatus(v || '')}
                  placeholder="所有狀態" allowClear style={{ width: 120 }}>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <Select.Option key={k} value={k}>{v.label}</Select.Option>
            ))}
          </Select>
          <Select value={filterType || undefined} onChange={v => setFilterType(v || '')}
                  placeholder="所有類型" allowClear style={{ width: 110 }}>
            {Object.entries(TYPE_CONFIG).map(([k, v]) => (
              <Select.Option key={k} value={k}>{v.label}</Select.Option>
            ))}
          </Select>
          <Select value={filterPriority || undefined} onChange={v => setFilterPriority(v || '')}
                  placeholder="所有優先級" allowClear style={{ width: 120 }}>
            {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
              <Select.Option key={k} value={k}>{v.label}</Select.Option>
            ))}
          </Select>
          <Button icon={<ReloadOutlined />} onClick={fetchAll} loading={loading}>重新整理</Button>
        </Space>
      </Card>

      <Card bodyStyle={{ padding: 0 }}>
        <Table columns={columns} dataSource={items} rowKey="id" loading={loading}
               size="middle" pagination={{ pageSize: 30, showTotal: t => `共 ${t} 筆` }} />
      </Card>

      {/* Create Modal */}
      <Modal title={<><PlusOutlined /> 新增需求 / Bug</>} open={createModal}
             onCancel={() => setCreateModal(false)}
             onOk={() => form.submit()} confirmLoading={submitting}
             okText="提交" cancelText="取消" width={700} destroyOnClose>
        <Form form={form} layout="vertical" onFinish={handleCreate} style={{ marginTop: 16 }}>
          <FormContent type="create" />
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal title={<><EditOutlined /> 編輯記錄</>} open={editModal}
             onCancel={() => setEditModal(false)}
             onOk={() => editForm.submit()} confirmLoading={submitting}
             okText="儲存" cancelText="取消" width={700} destroyOnClose>
        <Form form={editForm} layout="vertical" onFinish={handleEdit} style={{ marginTop: 16 }}>
          <FormContent type="edit" />
        </Form>
      </Modal>

      {/* Detail Drawer */}
      <Drawer
        title={detailDrawer ? (
          <Space>
            <Tag color={TYPE_CONFIG[detailDrawer.type]?.color}>
              {TYPE_CONFIG[detailDrawer.type]?.icon} {TYPE_CONFIG[detailDrawer.type]?.label}
            </Tag>
            <span>#{detailDrawer.id}</span>
          </Space>
        ) : '詳情'}
        open={!!detailDrawer}
        onClose={() => setDetailDrawer(null)}
        width="50%"
        extra={detailDrawer && (
          <Space>
            <Tooltip title="複製記錄">
              <Button icon={<CopyOutlined />} size="small" onClick={() => handleCopyRecord(detailDrawer)} />
            </Tooltip>
            <Tooltip title="複製 AI Prompt（貼給 Claude/ChatGPT 開發）">
              <Button icon={<RobotOutlined />} size="small" type="primary"
                      onClick={() => handleCopyPrompt(detailDrawer)}>
                複製 AI Prompt
              </Button>
            </Tooltip>
            {isStaff && <Button icon={<EditOutlined />} size="small" onClick={openEdit}>編輯</Button>}
            {isStaff && (
              <Popconfirm title="刪除此記錄？" onConfirm={() => handleDelete(detailDrawer.id)}>
                <Button icon={<DeleteOutlined />} size="small" danger />
              </Popconfirm>
            )}
          </Space>
        )}
      >
        {detailDrawer && (
          <div>
            <Title level={5}>{detailDrawer.title}</Title>
            <Space wrap style={{ marginBottom: 12 }}>
              <Tag color={PRIORITY_CONFIG[detailDrawer.priority]?.color}>
                {PRIORITY_CONFIG[detailDrawer.priority]?.label}
              </Tag>
              <Tag color={STATUS_CONFIG[detailDrawer.status]?.color}>
                {STATUS_CONFIG[detailDrawer.status]?.label}
              </Tag>
              {detailDrawer.tags?.map(t => <Tag key={t} icon={<TagOutlined />}>{t}</Tag>)}
            </Space>

            {detailDrawer.reporter_name && (
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                <UserOutlined /> {detailDrawer.reporter_name} ·{' '}
                {new Date(detailDrawer.created_at).toLocaleString('zh-TW')}
                {detailDrawer.assignee && ` · 指派：${detailDrawer.assignee}`}
              </Text>
            )}
            {detailDrawer.page_url && (
              <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
                頁面：<code>{detailDrawer.page_url}</code>
              </Text>
            )}

            {detailDrawer.description && (
              <>
                <Divider orientation="left">描述</Divider>
                <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{detailDrawer.description}</Paragraph>
              </>
            )}
            {detailDrawer.steps_to_reproduce && (
              <>
                <Divider orientation="left">重現步驟</Divider>
                <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{detailDrawer.steps_to_reproduce}</Paragraph>
              </>
            )}
            {detailDrawer.current_behavior && (
              <>
                <Divider orientation="left">目前行為</Divider>
                <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{detailDrawer.current_behavior}</Paragraph>
              </>
            )}
            {detailDrawer.expected_behavior && (
              <>
                <Divider orientation="left">預期行為</Divider>
                <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{detailDrawer.expected_behavior}</Paragraph>
              </>
            )}

            {/* AI Prompt Preview */}
            <Divider orientation="left"><RobotOutlined /> AI Prompt 預覽</Divider>
            <Alert
              type="info" showIcon
              message="點擊「複製 AI Prompt」可直接將此內容貼入 Claude Code / ChatGPT 請它開發"
              style={{ marginBottom: 8, fontSize: 12 }}
            />
            <pre style={{
              background: '#1e1e1e', color: '#d4d4d4', padding: 12, borderRadius: 8,
              fontSize: 11, whiteSpace: 'pre-wrap', maxHeight: 240, overflow: 'auto',
            }}>
              {generateAIPrompt(detailDrawer)}
            </pre>

            {/* Comments */}
            <Divider orientation="left"><MessageOutlined /> 留言 ({detailDrawer.comments?.length || 0})</Divider>
            {detailDrawer.comments?.length > 0 && (
              <List
                dataSource={detailDrawer.comments}
                style={{ marginBottom: 12 }}
                renderItem={c => (
                  <List.Item style={{ padding: '8px 0' }}>
                    <List.Item.Meta
                      avatar={<Avatar icon={<UserOutlined />} size="small" />}
                      title={<><Text strong style={{ fontSize: 12 }}>{c.author_name}</Text>
                        <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                          {new Date(c.created_at).toLocaleString('zh-TW')}
                        </Text></>}
                      description={<Text style={{ fontSize: 13 }}>{c.content}</Text>}
                    />
                  </List.Item>
                )}
              />
            )}
            <Space.Compact style={{ width: '100%' }}>
              <Input value={commentInput} onChange={e => setCommentInput(e.target.value)}
                     onPressEnter={handleComment} placeholder="加入留言..." />
              <Button type="primary" onClick={handleComment} loading={submitting}
                      icon={<MessageOutlined />}>送出</Button>
            </Space.Compact>
          </div>
        )}
      </Drawer>
    </div>
  );
}
